import { Button, Modal, Steps, Tag, message } from "antd";
import { useCallback, useContext, useState } from "react";
import ProCard from "@ant-design/pro-card";
import ScheduleBulkAdd from "./schedule-bulkadd";
import { PageContainer } from "@ant-design/pro-layout";
import ScheduleBulkAddSummary from "./schedule-bulkadd-summary";
import {
  BulkSchedule,
  BulkScheduleList,
  DeletedDraftSchedulePreview,
  UpdatedDraftSchedulePreview,
} from "./schedule-bulkadd-entity";
import { EmployeeDetails } from "../../../serviceoperation/entity/EmployeeOperationEntity";
import { ScheduleOperation } from "../../../serviceoperation/ScheduleOperation";
import { CompanyContext } from "../../../contexts/company-context";
import { useNavigate } from "react-router";

const ScheduleBulkAddLayout = (): JSX.Element => {
  const defaultSelectedKey = 0;
  const [currentStep, setCurrentStep] = useState<number>(defaultSelectedKey);
  const [scheduleData, setScheduleData] = useState<BulkScheduleList[]>([]);
  const [employee, SetEmployee] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [nextDisabled, setNextDisabled] = useState<boolean>(true);
  const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetails[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string[]>([]);
  const { selectedCompany } = useContext(CompanyContext);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const [loadings, setLoadings] = useState<boolean>();
  const navigate = useNavigate();

  const [finalScheduleData, setFinalScheduleData] = useState<BulkSchedule[]>(
    [],
  );

  const [notifyEmployee, setNotifyEmployee] = useState<boolean>(false);
  const [isDraft, setIsDraft] = useState<boolean>(false);
  const [selectedTaskTemplateId, setSelectedTaskTemplateId] = useState<
    string | null
  >(null);
  const [deletedDraftScheduleIds, setDeletedDraftScheduleIds] = useState<
    number[]
  >([]);
  const [deletedDraftSchedules, setDeletedDraftSchedules] = useState<
    DeletedDraftSchedulePreview[]
  >([]);
  const [updatedDraftSchedules, setUpdatedDraftSchedules] = useState<
    UpdatedDraftSchedulePreview[]
  >([]);

  const handleNofityEmployee = (value: boolean) => {
    setNotifyEmployee(value);
  };

  const stepLength = 2;

  const handleStepChange = (value: number) => {
    setCurrentStep(value);
  };

  const saveScheduleData = useCallback(
    (
      data: BulkScheduleList[],
      employeeData: { value: string; label: string }[],
      employeeDetails: EmployeeDetails[],
      selectedWeek: string[],
      deletedIds?: number[],
      deletedSchedules?: DeletedDraftSchedulePreview[],
      updatedSchedules?: UpdatedDraftSchedulePreview[],
    ) => {
      setScheduleData(data);
      SetEmployee(employeeData);
      setEmployeeDetails(employeeDetails);
      setSelectedWeek(selectedWeek);
      if (deletedIds !== undefined) {
        setDeletedDraftScheduleIds((prev) =>
          Array.from(new Set([...prev, ...deletedIds])),
        );
      }

      if (deletedSchedules !== undefined) {
        setDeletedDraftSchedules((prev) =>
          Array.from(
            new Map(
              [...prev, ...deletedSchedules].map((item) => [
                item.scheduleId,
                item,
              ]),
            ).values(),
          ),
        );
      }

      if (updatedSchedules !== undefined) {
        setUpdatedDraftSchedules((prev) =>
          Array.from(
            new Map(
              [...prev, ...updatedSchedules].map((item) => [
                item.scheduleId,
                item,
              ]),
            ).values(),
          ),
        );
      }

      const hasDeletedChanges =
        (deletedIds?.length ?? 0) > 0 || deletedDraftScheduleIds.length > 0;
      const hasUpdatedChanges =
        (updatedSchedules?.length ?? 0) > 0 || updatedDraftSchedules.length > 0;
      setNextDisabled(
        !(data.length > 0 || hasDeletedChanges || hasUpdatedChanges),
      );
    },
    [currentStep, deletedDraftScheduleIds, updatedDraftSchedules],
  );

  const saveFinalScheduleData = useCallback(
    (
      data: BulkSchedule[],
      employeeDetails: EmployeeDetails[],
      notifyEmployee?: boolean,
      isDraft?: boolean,
      taskTemplateId?: string,
    ) => {
      setFinalScheduleData(data);
      handleNofityEmployee(notifyEmployee ?? false);
      setIsDraft(isDraft ?? false);
      setSelectedTaskTemplateId(taskTemplateId ?? null);
    },
    [],
  );

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const submitSchedule = async (forceDraft?: boolean) => {
    setLoadings(true);
    const scheduleOps = new ScheduleOperation();
    const effectiveIsDraft = forceDraft ?? isDraft;
    const effectiveNotifyEmployee = effectiveIsDraft ? false : notifyEmployee;

    try {
      const corpId = selectedCompany?.corporationId;
      if (!corpId) {
        message.error("Unable to process request. Please re-select a company.");
        return;
      }

      const insertSchedules = finalScheduleData
        .map((employeeSchedule) => ({
          employeeId: employeeSchedule.name,
          occurrences: employeeSchedule.schedule
            .filter((schedule) => schedule.scheduleId === undefined)
            .map((schedule) => ({
              title: schedule.title,
              note: schedule.description,
              workDay: schedule.date,
              startWorkHour: Number(schedule.startHour),
              startWorkMinute: Number(schedule.startMin),
              endWorkHour: Number(schedule.endHour),
              endWorkMinute: Number(schedule.endMin),
              timeZoneId: timeZone,
              branchId: schedule.branchId,
              taskTemplateId:
                schedule.taskTemplateId ??
                employeeSchedule.taskTemplateId ??
                selectedTaskTemplateId ??
                undefined,
            })),
        }))
        .filter((employeeSchedule) => employeeSchedule.occurrences.length > 0);

      const rawDraftUpdates = finalScheduleData.flatMap((employeeSchedule) =>
        employeeSchedule.schedule
          .filter((schedule) => schedule.scheduleId !== undefined)
          .map((schedule) => ({
            scheduleId: schedule.scheduleId as number,
            workDay: schedule.date,
            startWorkHour: Number(schedule.startHour),
            startWorkMinute: Number(schedule.startMin),
            endWorkHour: Number(schedule.endHour),
            endWorkMinute: Number(schedule.endMin),
            title: schedule.title,
            note: schedule.description,
            branchId: schedule.branchId,
            timeZoneId: timeZone,
            taskTemplateIds:
              schedule.taskTemplateId ||
              employeeSchedule.taskTemplateId ||
              selectedTaskTemplateId
                ? [
                    (schedule.taskTemplateId ||
                      employeeSchedule.taskTemplateId ||
                      selectedTaskTemplateId) as string,
                  ]
                : undefined,
          })),
      );

      const dedupedDraftUpdates = Array.from(
        new Map(rawDraftUpdates.map((x) => [x.scheduleId, x])).values(),
      );

      const dedupedDraftDeletes = Array.from(new Set(deletedDraftScheduleIds));

      if (
        insertSchedules.length === 0 &&
        dedupedDraftUpdates.length === 0 &&
        dedupedDraftDeletes.length === 0
      ) {
        message.info("No schedule changes detected.");
        return;
      }

      let insertSuccessful = false;
      let updateSuccessful = false;
      let deleteSuccessful = false;
      let hasFailure = false;
      const failureMessages: string[] = [];

      if (dedupedDraftDeletes.length > 0) {
        const deleteResponse = await scheduleOps.deleteBulkDraftScheduleAsync(
          corpId,
          {
            scheduleIds: dedupedDraftDeletes,
          },
        );

        const deletedCount = deleteResponse.data?.deleted ?? 0;
        const failedCount = deleteResponse.data?.failed ?? 0;

        if (deleteResponse.statusCode === 200 && deletedCount > 0) {
          deleteSuccessful = true;
        }

        if (deleteResponse.statusCode !== 200 || failedCount > 0) {
          hasFailure = true;
          const perItemFailures =
            deleteResponse.data?.results
              ?.filter((x) => !x.success)
              .map((x) => `#${x.scheduleId}: ${x.message}`) ?? [];

          if (perItemFailures.length > 0) {
            failureMessages.push(perItemFailures.slice(0, 3).join(" | "));
          } else {
            failureMessages.push(
              deleteResponse.statusMessage ||
                "Failed to delete one or more draft schedules.",
            );
          }
        }
      }

      if (insertSchedules.length > 0) {
        const insertResponse = await scheduleOps.postBulkScheduleAsync(corpId, {
          isDraft: effectiveIsDraft,
          schedules: insertSchedules,
          notification: {
            notifyEmployee: effectiveNotifyEmployee,
            title: "Schedule Posted!",
            body: `${selectedWeek[0]} - ${selectedWeek[6]}`,
          },
        });

        if (insertResponse.statusCode === 201) {
          insertSuccessful = true;
        } else {
          hasFailure = true;
          failureMessages.push(
            insertResponse.statusMessage || "Failed to create new schedules.",
          );
        }
      }

      if (dedupedDraftUpdates.length > 0) {
        const updateResponse = await scheduleOps.putBulkDraftScheduleAsync(
          corpId,
          {
            schedules: dedupedDraftUpdates,
          },
        );

        const updatedCount = updateResponse.data?.updated ?? 0;
        const failedCount = updateResponse.data?.failed ?? 0;

        if (updateResponse.statusCode === 200 && updatedCount > 0) {
          updateSuccessful = true;
        }

        if (updateResponse.statusCode !== 200 || failedCount > 0) {
          hasFailure = true;
          const perItemFailures =
            updateResponse.data?.results
              ?.filter((x) => !x.success)
              .map((x) => `#${x.scheduleId}: ${x.message}`) ?? [];

          if (perItemFailures.length > 0) {
            failureMessages.push(perItemFailures.slice(0, 3).join(" | "));
          } else {
            failureMessages.push(
              updateResponse.statusMessage ||
                "Failed to update one or more draft schedules.",
            );
          }
        }
      }

      const anySuccess =
        insertSuccessful || updateSuccessful || deleteSuccessful;

      if (hasFailure && anySuccess) {
        message.warning(
          "Draft schedule processing completed with some failures.",
        );
      } else if (hasFailure) {
        message.error(
          failureMessages[0] ||
            "There was an error trying to process your request!",
        );
      } else {
        navigate(`/schedule`);
        message.success("Schedule successfully processed!");
      }
    } catch (error) {
      message.error(
        "There was an error trying to process your request! Please refresh your page and try again!",
      );
    } finally {
      setLoadings(false);
    }
  };

  const renderComponent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ScheduleBulkAdd
            scheduleData={scheduleData}
            onSuccess={saveScheduleData}
            employeeData={employee}
          />
        );
      /*case 1:
        return (
          <ScheduleBulkAddDetails
            scheduleData={scheduleData}
            onSuccess={saveScheduleData}
            employeeData={employee}
          />
        );*/
      case 1:
        return (
          <ScheduleBulkAddSummary
            scheduleData={scheduleData}
            employeeData={[]}
            employeeDetails={employeeDetails}
            onSuccess={saveFinalScheduleData}
            selectedWeek={selectedWeek}
            deletedDraftSchedules={deletedDraftSchedules}
            updatedDraftSchedules={updatedDraftSchedules}
          />
        );
    }
  };

  const [openReset, setOpenReset] = useState(false);

  const resetClickHandle = () => {
    setOpenReset(true);
  };

  const handleResetOk = () => {
    setCurrentStep(defaultSelectedKey);
    setScheduleData([]);
    SetEmployee([]);
    setEmployeeDetails([]);
    setSelectedWeek([]);
    setFinalScheduleData([]);
    setNotifyEmployee(false);
    setIsDraft(false);
    setSelectedTaskTemplateId(null);
    setDeletedDraftScheduleIds([]);
    setDeletedDraftSchedules([]);
    setUpdatedDraftSchedules([]);
    setOpenReset(false);
  };

  return (
    <PageContainer
      extra={[
        <Button key="2" onClick={() => resetClickHandle()}>
          Reset
        </Button>,
        /*<Button key="1" type="primary">
          Save Draft
        </Button>,*/
      ]}
      title="Schedule Bulk Add"
      subTitle="Streamline the scheduling process for your team by bulk adding their schedules."
      footer={[
        <>
          {currentStep < stepLength - 1 && (
            <Button
              disabled={nextDisabled}
              type="primary"
              onClick={() => next()}
            >
              Next
            </Button>
          )}
          {currentStep === stepLength - 1 && (
            <>
              {isDraft ? (
                <Tag color="orange" style={{ marginRight: 8 }}>
                  Draft Mode Enabled
                </Tag>
              ) : null}
              <Button
                loading={loadings}
                onClick={() => {
                  setIsDraft(true);
                  setNotifyEmployee(false);
                  submitSchedule(true);
                }}
              >
                Save as Draft
              </Button>
              <Button
                type="primary"
                loading={loadings}
                onClick={() => submitSchedule()}
              >
                Submit
              </Button>
            </>
          )}
          {currentStep > 0 && <Button onClick={() => prev()}>Previous</Button>}
        </>,
      ]}
    >
      <ProCard>
        <Steps
          type="default"
          size="small"
          current={currentStep}
          onChange={handleStepChange}
          className="site-navigation-steps"
          items={[
            {
              title: "Step 1",
              subTitle: "DateTime selection",
              description: "Add schedule time",
              disabled: true,
            },
            //{
            //  title: "Step 2",
            //  subTitle: "Misc details",
            //  description: "Set employees schedule details.",
            //  disabled: true,
            //},
            {
              title: "Step 2",
              subTitle: "Summary",
              description: "Validate and submit the schedule!",
              disabled: true,
            },
          ]}
        />
        {renderComponent()}
      </ProCard>
      <Modal
        title="Reset?"
        open={openReset}
        onOk={handleResetOk}
        onCancel={() => setOpenReset(false)}
      >
        <p>
          Are you certain you wish to initiate a reset for the current form?
        </p>
      </Modal>
    </PageContainer>
  );
};

export default ScheduleBulkAddLayout;
